import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ChevronLeft, Loader2, Calendar, BookOpen, ArrowRight, Home, MessageCircle } from 'lucide-react';

import NotFound from '@/pages/NotFound';
import { useStaticPage } from '@/hooks/useStaticPage';
import { fadeUp, viewportOnce } from '@/lib/motion';
import { PrimaryButton, SecondaryButton } from '@/components/brand/Buttons';

const EASE = [0.22, 1, 0.36, 1];

function formatUpdated(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return ''; }
}

/* ─── Built-in default content for every T&C / legal page ───────────────── */
const DEFAULTS = {
  'content-guidelines': {
    title: 'Content Guidelines',
    lastUpdated: '1 June 2026',
    eyebrow: 'Legal & Compliance',
    intro:
      'These Content Guidelines set out the standards that apply to all content published on, through, or in connection with Max Global Software ("MGS", "we", "us") platforms, products, and services. By accessing or contributing content, you agree to these guidelines.',
    sections: [
      {
        id: 'acceptable-use',
        heading: 'Acceptable Use',
        body: 'Content published through MGS platforms must be lawful, accurate, and respectful. You may not use our platforms to publish, distribute, or facilitate content that:\n\n• Is defamatory, abusive, hateful, or discriminatory on the basis of race, ethnicity, gender, religion, nationality, disability, sexual orientation, or age.\n• Is fraudulent, deceptive, or misleading — including false claims about products, services, or individuals.\n• Infringes the intellectual property rights of any third party, including copyrights, trademarks, patents, or trade secrets.\n• Contains malware, viruses, or any code designed to damage, disrupt, or gain unauthorised access to systems.\n• Violates any applicable law or regulation, including export control laws, privacy laws, and anti-spam legislation.',
      },
      {
        id: 'content-standards',
        heading: 'Content Standards',
        body: 'All content submitted to or through MGS services must meet the following quality standards:\n\n• Accuracy: Content must be factually correct to the best of your knowledge. Where content is opinion or interpretation, it must be clearly presented as such.\n• Clarity: Content must be written in clear, professional language appropriate to the intended audience.\n• Relevance: Content must be relevant to the context in which it is published and must not include unsolicited promotional material (spam).\n• Attribution: Where content references third-party sources, those sources must be appropriately cited and credited.\n• Completeness: Content must not omit material facts that would be necessary for a reader to form an accurate understanding.',
      },
      {
        id: 'intellectual-property',
        heading: 'Intellectual Property',
        body: 'Respect for intellectual property is fundamental to how MGS operates.\n\n• You retain ownership of original content you create and submit to MGS platforms, but by submitting content you grant MGS a non-exclusive, worldwide, royalty-free licence to use, reproduce, and display that content in connection with the provision of our services.\n• You must not submit content that incorporates third-party copyrighted material without appropriate permission or a valid fair-use justification.\n• MGS trademarks, logos, brand assets, and product names may not be used without prior written permission from Max Global Software.\n• If you believe content on an MGS platform infringes your intellectual property rights, please contact us at legal@maxglobalsoft.com with details of your claim.',
      },
      {
        id: 'user-responsibilities',
        heading: 'User Responsibilities',
        body: 'When you access or contribute to any MGS platform, you are responsible for:\n\n• Ensuring all content you submit complies with these guidelines and all applicable laws.\n• Maintaining the confidentiality of your account credentials and promptly notifying MGS of any suspected unauthorised access.\n• Not attempting to circumvent any technical measures MGS uses to enforce these guidelines.\n• Not collecting or harvesting data from MGS platforms through automated means without prior written permission.\n• Promptly removing or correcting any content you have submitted that you later discover to be inaccurate, misleading, or non-compliant with these guidelines.',
      },
      {
        id: 'prohibited-content',
        heading: 'Prohibited Content',
        body: 'The following categories of content are strictly prohibited on all MGS platforms and services:\n\n• Adult content, explicit material, or content that sexualises minors in any form.\n• Threats of violence, harassment, or stalking directed at any individual or group.\n• Content that promotes, glorifies, or facilitates terrorism, extremism, or political violence.\n• Personal data of third parties shared without their consent, including identity documents, financial information, or medical records.\n• Unauthorised recordings or captures of private communications.\n• Content designed to manipulate platform algorithms or metrics, including artificial engagement, fake reviews, or coordinated inauthentic behaviour.',
      },
      {
        id: 'reporting',
        heading: 'Reporting Violations',
        body: 'If you encounter content on any MGS platform that you believe violates these guidelines, please report it to us at:\n\nEmail: info@maxglobalsoft.com\nSubject line: "Content Guideline Concern"\n\nPlease include in your report: a description of the content in question, the location (URL or platform section) where the content appears, and the specific guideline you believe has been violated.\n\nMGS will review all reports and, where a violation is confirmed, take appropriate action, which may include content removal, account suspension, or referral to law enforcement authorities.',
      },
      {
        id: 'enforcement',
        heading: 'Enforcement',
        body: 'MGS reserves the right to remove, restrict, or refuse any content that violates these guidelines, at our sole discretion and without prior notice. Repeated or serious violations may result in permanent termination of access to MGS platforms and services.\n\nWhere violations involve potential criminal activity or threats to safety, MGS will cooperate fully with law enforcement authorities.',
      },
      {
        id: 'updates',
        heading: 'Updates to These Guidelines',
        body: 'MGS may update these Content Guidelines from time to time to reflect changes in our services, applicable law, or community standards. The date of the most recent revision is shown at the top of this page.\n\nContinued use of MGS platforms following the posting of updated guidelines constitutes your acceptance of those updates. We encourage you to review these guidelines periodically.',
      },
    ],
  },

  'privacy-policy': {
    title: 'Privacy Policy',
    lastUpdated: '1 June 2026',
    eyebrow: 'Legal & Compliance',
    intro:
      'Max Global Software ("MGS", "we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data when you visit our website, use our services, or interact with us.',
    sections: [
      {
        id: 'data-we-collect',
        heading: 'Data We Collect',
        body: 'We collect personal data that you provide to us directly and data that is generated automatically through your use of our services:\n\n• Identity Data: name, job title, company name.\n• Contact Data: email address, phone number, mailing address.\n• Technical Data: IP address, browser type and version, time zone, device identifiers, cookies and similar tracking technologies.\n• Usage Data: information about how you use our website and services.\n• Communications Data: records of correspondence if you contact us.\n• Marketing Data: your preferences in receiving communications from us.\n\nWe do not collect special category data (such as health, biometric, or racial/ethnic origin data) unless expressly required for a contracted service and with your explicit consent.',
      },
      {
        id: 'how-we-use-data',
        heading: 'How We Use Your Data',
        body: 'We use your personal data for the following purposes:\n\n• To deliver and improve our products and services.\n• To communicate with you about your enquiries, projects, and account.\n• To send marketing communications where you have consented or we have a legitimate interest.\n• To comply with our legal obligations.\n• To prevent fraud and ensure the security of our platforms.\n• To carry out analytics and improve the performance and usability of our website.\n\nWe rely on legitimate interest, contractual necessity, legal obligation, or consent as our lawful basis for processing, depending on the specific use.',
      },
      {
        id: 'data-sharing',
        heading: 'Sharing Your Data',
        body: 'We do not sell your personal data. We may share your data with:\n\n• Service providers who help us deliver our services (cloud hosting, analytics, CRM, payment processing) — bound by confidentiality agreements.\n• Professional advisers (lawyers, accountants, auditors) under duties of professional confidentiality.\n• Regulatory authorities or law enforcement bodies where required by law.\n• A buyer or successor entity in the event of a merger, acquisition, or sale of assets.\n\nAll third parties are required to respect the security of your personal data and treat it in accordance with applicable law.',
      },
      {
        id: 'data-retention',
        heading: 'Data Retention',
        body: 'We retain your personal data for as long as necessary to fulfil the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements.\n\nWhere we no longer need your data, we will securely delete or anonymise it. Typically, client data is retained for 7 years from the end of the business relationship for tax and accounting purposes.',
      },
      {
        id: 'your-rights',
        heading: 'Your Rights',
        body: 'Depending on your location, you may have the following rights regarding your personal data:\n\n• Right to access: request a copy of the data we hold about you.\n• Right to rectification: request correction of inaccurate data.\n• Right to erasure: request deletion of your data in certain circumstances.\n• Right to restriction: request that we restrict processing of your data.\n• Right to data portability: receive your data in a structured, machine-readable format.\n• Right to object: object to processing based on legitimate interests.\n• Right to withdraw consent: where processing is based on consent, withdraw it at any time.\n\nTo exercise any of these rights, email us at privacy@maxglobalsoft.com.',
      },
      {
        id: 'cookies',
        heading: 'Cookies',
        body: 'Our website uses cookies and similar tracking technologies to enhance your experience and analyse site usage. We use:\n\n• Strictly necessary cookies: required for the website to function.\n• Analytics cookies: help us understand how visitors use our site.\n• Marketing cookies: used to deliver relevant advertising (only with consent).\n\nYou can control cookies through your browser settings. Blocking certain cookies may affect your ability to use some features of our website.',
      },
      {
        id: 'contact-dpo',
        heading: 'Contact & Data Protection',
        body: 'If you have questions about this Privacy Policy or how we handle your personal data, please contact:\n\nMax Global Software\nSamartha Apartment, #202, 18th Main Road, Kumaraswamy Layout, Bengaluru – 560078\nEmail: privacy@maxglobalsoft.com\n\nIf you are in the EU/UK and are not satisfied with our response, you have the right to lodge a complaint with your local data protection authority.',
      },
    ],
  },

  'legal-disclosure': {
    title: 'Legal Disclosure',
    lastUpdated: '1 June 2026',
    eyebrow: 'Legal & Compliance',
    intro:
      'This Legal Disclosure provides information about Max Global Software in accordance with applicable legal and regulatory requirements. Please read this page carefully.',
    sections: [
      {
        id: 'company-information',
        heading: 'Company Information',
        body: 'Max Global Software\nSamartha Apartment, #202, Door No. 2405,\n18th Main Road, Kumaraswamy Layout 2nd Stage,\nBengaluru – 560078, Karnataka, India\n\nFounded: 2005\nManaging Director: Bhupendra Verma\n\nPhone: +91-7795287075 | +91-80-49746380\nEmail: info@maxglobalsoft.com\nWebsite: www.maxglobalsoft.com',
      },
      {
        id: 'scope-of-services',
        heading: 'Scope of Services',
        body: 'Max Global Software provides software engineering, artificial intelligence, machine learning, web and mobile development, ERP solutions, UI/UX design, and related consulting and professional services to businesses worldwide.\n\nService engagements are governed by individual contracts or statements of work agreed between MGS and the respective client. Nothing on this website constitutes a binding offer or contractual commitment.',
      },
      {
        id: 'disclaimer',
        heading: 'Disclaimer of Warranties',
        body: 'The information on this website is provided for general informational purposes only. While we endeavour to keep the information accurate and current, we make no representations or warranties of any kind — express or implied — about the completeness, accuracy, reliability, or suitability of any information, products, or services referenced on this website.\n\nCase studies and project outcomes described on this website reflect past performance and do not guarantee future results. Client names, metrics, and project details may be anonymised or aggregated.',
      },
      {
        id: 'limitation-of-liability',
        heading: 'Limitation of Liability',
        body: 'To the fullest extent permitted by law, Max Global Software shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including loss of profits, data, or goodwill — arising from your use of or inability to use this website or our services.\n\nOur total aggregate liability for any claim arising from use of this website shall not exceed INR 10,000 (ten thousand Indian rupees).',
      },
      {
        id: 'third-party-links',
        heading: 'Third-Party Links',
        body: 'This website may contain links to external websites that are not operated by Max Global Software. We have no control over the content or privacy practices of those sites and accept no responsibility for them. The inclusion of any link does not constitute endorsement of the linked site.',
      },
      {
        id: 'governing-law',
        heading: 'Governing Law',
        body: 'These disclosures and any disputes arising from them are governed by the laws of Karnataka, India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka.',
      },
    ],
  },

  'security': {
    title: 'Security',
    lastUpdated: '1 June 2026',
    eyebrow: 'Trust & Safety',
    intro:
      'At Max Global Software, security is an engineering discipline — not a checkbox. This page explains how we protect our own systems and the systems we build for clients, and how you can report a security concern.',
    sections: [
      {
        id: 'our-approach',
        heading: 'Our Security Approach',
        body: 'Security at MGS follows a defence-in-depth model across people, process, and technology:\n\n• We adopt a Security-by-Design philosophy: security requirements are defined at the architecture stage of every project, not added as an afterthought.\n• All engineers receive annual security awareness training and role-specific secure coding training.\n• We conduct internal code reviews and SAST/DAST scans as part of our standard development workflow.\n• Critical systems and client-facing infrastructure are subject to periodic third-party penetration testing.',
      },
      {
        id: 'infrastructure-security',
        heading: 'Infrastructure Security',
        body: 'MGS internal systems and client infrastructure we manage are protected by:\n\n• Encryption in transit (TLS 1.2+) and at rest (AES-256) for all data.\n• Multi-factor authentication (MFA) enforced for all production system access.\n• Role-based access control (RBAC) with least-privilege principles — access is granted only as required for the specific role.\n• Automated vulnerability scanning of dependencies and container images.\n• Centralised logging, monitoring, and alerting with defined incident response procedures.\n• Regular automated backups with tested restoration procedures.',
      },
      {
        id: 'client-project-security',
        heading: 'Security in Client Projects',
        body: 'For software we build for clients, our standard security practices include:\n\n• Threat modelling and security requirements definition at project inception.\n• OWASP Top-10 aligned secure coding standards enforced through code review.\n• Automated SAST (Static Application Security Testing) integrated into CI/CD pipelines.\n• API security: authentication (OAuth 2.0 / JWT), rate limiting, input validation, and output encoding.\n• Dependency vulnerability scanning with policy-based blocking of critical CVEs.\n• Pre-launch security review and, where scope warrants, third-party penetration testing.\n• Security-oriented handoff documentation including architecture decisions, threat model, and known risk register.',
      },
      {
        id: 'data-protection',
        heading: 'Data Protection & Privacy',
        body: 'MGS handles client and project data with strict confidentiality:\n\n• All project data is subject to confidentiality provisions in our contracts.\n• We process personal data in accordance with our Privacy Policy and applicable data protection law (GDPR, DPDP Act 2023).\n• Client environments are segregated; engineers only access data required for their specific engagement.\n• We use anonymised or synthetic data for development and testing where possible.',
      },
      {
        id: 'vulnerability-disclosure',
        heading: 'Responsible Disclosure',
        body: 'If you believe you have discovered a security vulnerability in an MGS website, product, or client system we manage, please report it responsibly:\n\nEmail: security@maxglobalsoft.com\nSubject: "Responsible Disclosure — [brief description]"\n\nPlease include: a description of the vulnerability, the affected system or URL, steps to reproduce, and the potential impact.\n\nWe commit to acknowledging your report within 2 business days and keeping you informed of our remediation progress. We ask that you do not publicly disclose the vulnerability until we have had a reasonable opportunity to investigate and remediate it.',
      },
      {
        id: 'certifications',
        heading: 'Compliance & Certifications',
        body: 'MGS engineering practices are aligned with:\n\n• OWASP Application Security Verification Standard (ASVS)\n• ISO 27001 principles for information security management\n• GDPR and India\'s Digital Personal Data Protection Act 2023\n• PCI-DSS requirements for projects involving payment card data\n• HIPAA guidelines for projects involving healthcare data\n\nWe are happy to discuss specific compliance requirements for your project. Contact us at info@maxglobalsoft.com.',
      },
    ],
  },

  'terms-of-service': {
    title: 'Terms of Service',
    lastUpdated: '1 June 2026',
    eyebrow: 'Legal & Compliance',
    intro:
      'These Terms of Service ("Terms") govern your access to and use of the website and services provided by Max Global Software ("MGS", "we", "us"). By accessing this website or engaging our services, you agree to be bound by these Terms.',
    sections: [
      {
        id: 'acceptance',
        heading: 'Acceptance of Terms',
        body: 'By accessing this website or using MGS services, you confirm that you are at least 18 years of age and have the legal capacity to enter into a binding agreement. If you are accessing this website on behalf of a company or organisation, you represent that you have authority to bind that entity to these Terms.\n\nIf you do not agree with any part of these Terms, you must not use this website or our services.',
      },
      {
        id: 'services',
        heading: 'Services',
        body: 'MGS provides software engineering, AI development, consulting, and related professional services. The specific scope, timeline, deliverables, fees, and terms of any engagement are set out in a separate Statement of Work (SOW) or Master Service Agreement (MSA) agreed in writing between MGS and the client.\n\nNothing on this website constitutes a binding offer, quotation, or commitment to provide services. All project engagements commence only upon execution of a written agreement.',
      },
      {
        id: 'website-use',
        heading: 'Website Use',
        body: 'This website is provided for general informational purposes. You may access and browse the website for lawful personal or commercial research purposes.\n\nYou may not:\n• Use automated tools (bots, scrapers) to extract content from the website without written permission.\n• Use the website in any way that could damage, overload, or impair its performance.\n• Attempt to gain unauthorised access to any part of the website, its servers, or associated databases.\n• Use the website to send unsolicited commercial communications.',
      },
      {
        id: 'intellectual-property',
        heading: 'Intellectual Property',
        body: 'All content on this website — including text, graphics, logos, images, case studies, and code samples — is the property of Max Global Software or its content licensors and is protected by intellectual property law.\n\nYou may view, download, and print content for your personal non-commercial use. You may not reproduce, distribute, modify, or create derivative works from website content without our prior written consent.\n\nFor client projects, intellectual property ownership is defined in the relevant SOW or MSA. Unless otherwise agreed in writing, MGS retains ownership of all proprietary tools, frameworks, and methodologies used in delivery.',
      },
      {
        id: 'payment',
        heading: 'Payment Terms',
        body: 'For professional service engagements, payment terms are as specified in the relevant SOW or MSA. Unless otherwise agreed:\n\n• Invoices are due within 30 days of the invoice date.\n• Late payments may attract interest at 1.5% per month on the overdue balance.\n• MGS reserves the right to suspend work on any project where invoices are more than 30 days overdue.\n• Disputed invoices must be raised in writing within 14 days of the invoice date.',
      },
      {
        id: 'limitation-of-liability',
        heading: 'Limitation of Liability',
        body: 'To the maximum extent permitted by applicable law:\n\n• MGS shall not be liable for indirect, incidental, special, consequential, or punitive damages.\n• MGS\'s total aggregate liability in relation to any engagement shall not exceed the total fees paid by the client to MGS in the 3 months preceding the event giving rise to the claim.\n• These limitations apply whether the claim arises in contract, tort (including negligence), or otherwise.\n\nSome jurisdictions do not allow limitation of liability for certain types of loss — in those jurisdictions, our liability is limited to the greatest extent permitted by law.',
      },
      {
        id: 'governing-law',
        heading: 'Governing Law & Disputes',
        body: 'These Terms are governed by the laws of Karnataka, India. Any dispute arising from or relating to these Terms shall first be subject to good-faith negotiation between the parties. If not resolved within 30 days, disputes shall be referred to arbitration in Bengaluru in accordance with the Arbitration and Conciliation Act, 1996.\n\nNothing in this clause prevents either party from seeking urgent injunctive relief from a court of competent jurisdiction.',
      },
      {
        id: 'changes',
        heading: 'Changes to These Terms',
        body: 'MGS may update these Terms from time to time. The date of the latest revision is shown at the top of this page. Continued use of this website after updated Terms are posted constitutes acceptance of those updates.\n\nFor active client engagements, changes to commercial terms require written agreement from both parties.',
      },
    ],
  },
};

/* ─── TOC Sidebar ─────────────────────────────────────────────────────────── */
function TOCSidebar({ sections, activeId }) {
  return (
    <aside className="hidden lg:block">
      <div className="sticky top-28">
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-brandblue mb-4">
          On This Page
        </p>
        <ul className="space-y-1.5">
          {sections.map((s) => (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={[
                  'block text-[13px] leading-snug py-1 pl-3 border-l-2 transition-all duration-200',
                  activeId === s.id
                    ? 'border-[#C9962E] text-navy-deep font-semibold'
                    : 'border-hairline text-slatebody hover:border-brandblue hover:text-navy-deep',
                ].join(' ')}
              >
                {s.heading}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

/* ─── Section block ───────────────────────────────────────────────────────── */
function SectionBlock({ section, index }) {
  const paragraphs = (section.body || '').split('\n\n').filter(Boolean);
  return (
    <motion.section
      id={section.id}
      variants={fadeUp}
      initial="hidden"
      whileInView="show"
      viewport={viewportOnce}
      transition={{ delay: index * 0.04 }}
      className="scroll-mt-28 pb-10 border-b border-hairline last:border-0 last:pb-0"
    >
      <h2 className="text-xl sm:text-2xl font-bold text-navy-deep mb-5 flex items-center gap-3">
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-brandblue/10 text-brandblue text-xs font-bold flex-shrink-0">
          {String(index + 1).padStart(2, '0')}
        </span>
        {section.heading}
      </h2>
      <div className="space-y-4">
        {paragraphs.map((p, i) => {
          const isBulletBlock = p.startsWith('•');
          if (isBulletBlock) {
            const lines = p.split('\n').filter(Boolean);
            return (
              <ul key={i} className="space-y-2">
                {lines.map((line, li) => (
                  <li key={li} className="flex gap-2 text-slatebody text-[15px] leading-relaxed">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#C9962E] flex-shrink-0" />
                    <span>{line.replace(/^•\s*/, '')}</span>
                  </li>
                ))}
              </ul>
            );
          }
          return (
            <p key={i} className="text-slatebody text-[15px] leading-relaxed whitespace-pre-line">
              {p}
            </p>
          );
        })}
      </div>
    </motion.section>
  );
}

/* ─── Hero ────────────────────────────────────────────────────────────────── */
function PageHeroSection({ eyebrow, title, lastUpdated, updatedAt }) {
  const displayDate = updatedAt ? formatUpdated(updatedAt) : lastUpdated;
  return (
    <section className="relative pt-48 pb-14 overflow-hidden">
      <div className="absolute inset-0 bg-sky-100" />
      <div className="absolute inset-0 bg-hero-mesh bg-[length:200%_200%] animate-mesh-pan opacity-70" />
      <svg className="absolute inset-0 w-full h-full opacity-[0.04] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="legaldots" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.1" fill="#2F6FB0" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#legaldots)" />
      </svg>

      <div className="container mx-auto relative">
        {/* Breadcrumb — left aligned */}
        <motion.nav
          aria-label="Breadcrumb"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="flex items-center gap-1.5 text-[12px] text-slatebody flex-wrap mb-8"
        >
          <Link to="/" className="hover:text-brandblue transition-colors">Home</Link>
          <ChevronRight className="h-3.5 w-3.5 text-slatebody/50" />
          <span className="font-semibold text-navy-deep">{title}</span>
        </motion.nav>

        {/* Centre-aligned heading block */}
        <div className="text-center">
          {/* LARGE: category / eyebrow — the primary headline */}
          {eyebrow && (
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-navy-deep leading-[1.06]"
            >
              {eyebrow}
            </motion.h1>
          )}

          {/* SMALLER: page-specific title */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15, ease: EASE }}
            className="mt-3 text-xl sm:text-2xl font-semibold text-brandblue tracking-tight"
          >
            {title}
          </motion.p>

          {/* Gradient divider — centred */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
            style={{ transformOrigin: 'center' }}
            className="mt-5 h-[2px] w-24 rounded-full bg-gradient-to-r from-[#2BA8E0] via-[#2F6FB0] to-[#E8C25A] mx-auto"
          />
        </div>

        {/* Last updated pill — left-aligned, below the heading block */}
        {displayDate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.45, ease: EASE }}
            className="mt-7 inline-flex items-center gap-2 text-[13px] text-slatebody bg-white/70 backdrop-blur-sm border border-hairline rounded-full px-4 py-1.5"
          >
            <Calendar className="h-3.5 w-3.5 text-brandblue" />
            Last updated:&nbsp;<span className="font-semibold text-navy-deep">{displayDate}</span>
          </motion.div>
        )}
      </div>
    </section>
  );
}

/* ─── Main page ───────────────────────────────────────────────────────────── */
export default function StaticPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data: cmsData, loading, notFound } = useStaticPage(slug);
  const [activeId, setActiveId] = useState('');
  const observerRef = useRef(null);

  const hasDefault = !!DEFAULTS[slug];
  const defaults = DEFAULTS[slug] || {};

  const page = {
    ...defaults,
    ...(cmsData
      ? Object.fromEntries(
          Object.entries(cmsData).filter(([, v]) => v !== null && v !== undefined && v !== '')
        )
      : {}),
  };

  const sections = Array.isArray(cmsData?.sections) && cmsData.sections.length
    ? cmsData.sections
    : defaults.sections || [];

  useEffect(() => {
    if (!page?.title) return;
    document.title = cmsData?.metaTitle || `${page.title} — Max Global Software`;
    if (cmsData?.metaDescription) {
      let el = document.querySelector('meta[name="description"]');
      if (!el) { el = document.createElement('meta'); el.setAttribute('name', 'description'); document.head.appendChild(el); }
      el.setAttribute('content', cmsData.metaDescription);
    }
  }, [page, cmsData]);

  useEffect(() => {
    if (!sections.length) return;
    const ids = sections.map((s) => s.id).filter(Boolean);
    if (!ids.length) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observerRef.current.observe(el);
    });
    return () => observerRef.current?.disconnect();
  }, [sections]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center pt-32 pb-20">
        <Loader2 className="h-6 w-6 animate-spin text-slatebody/60" />
      </div>
    );
  }

  if (notFound && !hasDefault) return <NotFound />;

  return (
    <article data-testid="static-page">
      <PageHeroSection
        eyebrow={page.eyebrow}
        title={page.title}
        lastUpdated={page.lastUpdated}
        updatedAt={cmsData?.updatedAt}
      />

      <div className="bg-white py-16">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-[220px_1fr] gap-14 xl:gap-20">
            <TOCSidebar sections={sections} activeId={activeId} />

            <div>
              {page.intro && (
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={viewportOnce}
                  className="mb-10 p-6 rounded-2xl bg-sky-50 border border-hairline"
                >
                  <div className="flex gap-3">
                    <BookOpen className="h-5 w-5 text-brandblue mt-0.5 flex-shrink-0" />
                    <p className="text-[15px] text-navy-deep/85 leading-relaxed">{page.intro}</p>
                  </div>
                </motion.div>
              )}

              <div className="space-y-10">
                {sections.map((s, i) => (
                  <SectionBlock key={s.id || i} section={s} index={i} />
                ))}
              </div>

              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="mt-14 pt-8 border-t border-hairline flex flex-col sm:flex-row items-center justify-between gap-4"
              >
                <SecondaryButton
                  icon={<ChevronLeft className="h-4 w-4" />}
                  onClick={() => navigate('/')}
                >
                  Back to Home
                </SecondaryButton>
                <PrimaryButton
                  icon={<MessageCircle className="h-4 w-4" />}
                  onClick={() => navigate('/contact')}
                >
                  Questions? Contact Us
                </PrimaryButton>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
